const MAGIC_LINK_EMAIL_KEY = "cognito_magic_link_email";

/**
 * What the redemption page needs to sign in silently on the same device: the
 * display email plus, when known, the concrete Cognito username auth was
 * initiated with. `authUsername` lets the redeem reuse the send-time resolution
 * instead of re-probing PreSignUp — essential for users whose email alias can't
 * initiate auth on its own (`email_verified=false`).
 */
export interface MagicLinkAuthContext {
  email: string;
  authUsername?: string;
}

interface MagicLinkEmailData {
  email: string;
  authUsername?: string;
  expiresAt: number;
}

interface MagicLinkEmailDataCandidate {
  email?: unknown;
  authUsername?: unknown;
  expiresAt?: unknown;
}

function isMagicLinkEmailData(data: unknown): data is MagicLinkEmailData {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const candidate = data as MagicLinkEmailDataCandidate;
  return (
    typeof candidate.email === "string" &&
    typeof candidate.expiresAt === "number" &&
    (candidate.authUsername === undefined || typeof candidate.authUsername === "string")
  );
}

/**
 * Reads the cached magic-link auth context (email + optionally the resolved
 * Cognito username). Returns `undefined` once the TTL has elapsed or the cache
 * is missing/corrupt. Tolerates the legacy shape where only a bare email string
 * was persisted.
 */
export function getMagicLinkAuthContext(): MagicLinkAuthContext | undefined {
  const raw = localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
  if (!raw) {
    return undefined;
  }

  try {
    const data: unknown = JSON.parse(raw);
    // Legacy shape: a bare email string with no TTL or username.
    if (typeof data === "string") {
      return { email: data };
    }

    if (!isMagicLinkEmailData(data)) {
      localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
      return undefined;
    }

    if (Date.now() > data.expiresAt) {
      localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
      return undefined;
    }

    return { email: data.email, authUsername: data.authUsername };
  } catch {
    localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
    return undefined;
  }
}

export function getMagicLinkEmail(): string | undefined {
  return getMagicLinkAuthContext()?.email;
}

export function clearMagicLinkEmail(): void {
  localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
}
